import { HostContext } from '../../embed-script/HostContext';

// A host page title over ~1 KB is already pathological; anything above this is truncated.
const MAX_TITLE_LENGTH = 1024;

// Hard ceiling on the whole serialized header. Above this an intermediary may truncate it
// mid-value, so we drop the unbounded host-supplied fields (and, if even that isn't enough, fall
// back to an empty context) rather than risk emitting a header a hop splits into invalid JSON
// (#2383). 8 KB leaves ample room under typical server header limits. This length check is in
// UTF-16 code units, which equals the on-the-wire byte count only because scrubHeaderValue caps its
// output at Latin-1 (one ISO-8859-1 byte per code point in fetch's header serialization) — so if the
// scrub ever changes to emit multi-byte code points (e.g. a base64/percent-encoding round-trip), this
// cap must switch to a byte count or it will silently under-count and weaken the #2383 guarantee.
const MAX_HEADER_LENGTH = 8192;

/**
 * Serializes a HostContext into a value that is safe to carry in the `AiAHostContext` HTTP request
 * header.
 *
 * The host page's <h1> (`currentPageTitle`) and other free-text fields are arbitrary content, and
 * `JSON.stringify` leaves several code points raw that are unsafe in an HTTP header value: it only
 * escapes the C0 controls (U+0000–U+001F), `"` and `\`, so the C1 control range (U+0080–U+009F)
 * and the Unicode line/paragraph separators (U+2028/U+2029) pass through verbatim. Such a
 * character truncated the header mid-string, so the backend saw invalid JSON and returned 500
 * instead of falling back to standalone (#2383). Code points above U+00FF additionally make
 * `Headers.append` throw, because a fetch header value is a Latin-1 byte string — that would fail
 * the request outright.
 *
 * We scrub those out here, at the single boundary every field and every source passes through.
 * Scrubbing the *stringified* JSON is safe: `JSON.stringify` has already escaped every structural
 * and C0 character to plain ASCII, so the only raw unsafe code points left live inside string
 * values, and removing them leaves the JSON well-formed. The one class the post-stringify scrub
 * can't reach is a lone surrogate in the raw input: `JSON.stringify` escapes it to an ASCII `\uXXXX`
 * the scrub would keep, and a strict backend parser (System.Text.Json) rejects it — so lone
 * surrogates are stripped *before* stringify (see `serializeSafe` / `stripLoneSurrogatesReplacer`).
 */
export const serializeHostContextHeader = (hostContext: HostContext): string => {
    // currentPageTitle is the host <h1> — arbitrary free text and the field most likely to be
    // large — so cap it first. Cap by code point (Array.from), not by UTF-16 code unit: a code-unit
    // slice can cut a surrogate pair in half, and a modern well-formed JSON.stringify then emits the
    // orphaned surrogate as the ASCII escape "\uXXXX" — which scrubHeaderValue keeps, because by then
    // it is plain ASCII, not the raw cp > 0xff the scrub drops. The result is valid-looking JSON
    // carrying a lone surrogate that a strict backend parser (System.Text.Json) rejects, re-opening
    // the #2383 broken-header path. Capping by code point keeps every pair whole, so the cap can't
    // orphan a surrogate at its boundary; a lone surrogate already present in the raw input (a sub-cap
    // title, or another host string/key) is handled separately by the pre-stringify strip in
    // serializeSafe. The typeof guard also rejects a null title (host init JSON can defy the
    // string | undefined type), which would otherwise throw on .length.
    const title = hostContext.currentPageTitle;
    const capped =
        typeof title === 'string' && title.length > MAX_TITLE_LENGTH
            ? { ...hostContext, currentPageTitle: Array.from(title).slice(0, MAX_TITLE_LENGTH).join('') }
            : hostContext;

    const header = serializeSafe(capped);
    if (header.length <= MAX_HEADER_LENGTH) {
        return header;
    }

    // Still oversized — customData and other host free-text fields are unbounded too, so the title
    // cap alone can't bound the header. Drop the arbitrary free-text fields (undefined values are
    // omitted by JSON.stringify), keeping the structured routing context, then re-check: the header
    // must never be emitted oversized, because a hop truncating it mid-value is the #2383 bug.
    const routing: HostContext = {
        ...capped,
        currentPageTitle: undefined,
        currentPagePath: undefined,
        currentPageAnchor: undefined,
        customData: undefined,
    };
    const routingHeader = serializeSafe(routing);
    if (routingHeader.length <= MAX_HEADER_LENGTH) {
        return routingHeader;
    }

    // Still oversized after the free-text fields are gone, so the overflow is in one of the many
    // unbounded, host-supplied identity/classification fields — hostname, vismaCustomerId,
    // contextSensitiveId, typeOfBusiness, companyType, activeBankIntegration, and whatever a future
    // HostContext adds. Enumerating each to drop is a blocklist: any field not remembered — and every
    // new field by default — survives past the products drop below, so a single bloated non-routing
    // field would strip product routing while itself riding along (the field, not products, was the
    // actual overflow). Keep only the routing-critical fields instead. products carries the tool-
    // discovery and handover weight the backend needs, so it stays in this tier and is dropped one step
    // lower. (Allowlist over blocklist — the same posture as the restyle link handling.)
    const routingOnly: HostContext = {
        id: routing.id,
        country: routing.country,
        language: routing.language,
        products: routing.products,
    };
    const routingOnlyHeader = serializeSafe(routingOnly);
    if (routingOnlyHeader.length <= MAX_HEADER_LENGTH) {
        return routingOnlyHeader;
    }

    // products is a host-supplied array with no size bound of its own, so it alone can overflow the
    // ceiling. Drop it, keeping just the fields that decide market and language so a Dutch user on a
    // pathological host page isn't silently defaulted to the backend's Country="SE" (#2398) — they're a
    // handful of bytes and can't be the bloat. id is itself host-supplied and unbounded, so re-check
    // once more: if it alone is the overflow, fall back to an empty context so the header is still
    // guaranteed under the ceiling. The backend treats either as standalone because no company data
    // survives (#2384).
    const floor = serializeSafe({
        id: routingOnly.id,
        country: routingOnly.country,
        language: routingOnly.language,
    });
    return floor.length <= MAX_HEADER_LENGTH ? floor : '{}';
};

/**
 * Removes the code points that cannot safely ride in an HTTP header value. See
 * {@link serializeHostContextHeader} for why each range is unsafe.
 */
export const scrubHeaderValue = (value: string): string =>
    // Array.from iterates by code point, so a surrogate pair (e.g. an emoji) is dropped as a whole
    // rather than leaving a lone surrogate behind.
    Array.from(value)
        .filter((ch) => {
            const cp = ch.codePointAt(0) ?? 0;
            return !(
                cp <= 0x1f || // C0 controls (incl. CR/LF — the header-injection chars)
                cp === 0x7f || // DEL
                (cp >= 0x80 && cp <= 0x9f) || // C1 controls (JSON.stringify leaves these raw)
                cp > 0xff // beyond Latin-1: Headers.append would throw; also covers U+2028/U+2029 and BOM
            );
        })
        .join('');

// A lone surrogate (an unpaired half of a pair) in raw host input can't be caught by scrubHeaderValue,
// which runs after JSON.stringify: a modern well-formed JSON.stringify emits the lone surrogate as the
// ASCII escape "\uXXXX", which is plain ASCII by then and so survives the scrub — and System.Text.Json
// rejects that escaped lone surrogate, re-opening #2383. Strip lone surrogates before serializing.
// Array.from iterates by code point, yielding a lone surrogate as a single element (cp 0xD800–0xDFFF)
// while a valid pair comes through as one element with cp > 0xFFFF.
const stripLoneSurrogates = (value: string): string =>
    Array.from(value)
        .filter((ch) => {
            const cp = ch.codePointAt(0) ?? 0;
            return !(cp >= 0xd800 && cp <= 0xdfff);
        })
        .join('');

// JSON.stringify replacer: strips lone surrogates from every host-supplied string value and every
// object key (customData keys are host-controlled too), at the single serialization boundary.
const stripLoneSurrogatesReplacer = (_key: string, value: unknown): unknown => {
    if (typeof value === 'string') {
        return stripLoneSurrogates(value);
    }
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        const sanitized: Record<string, unknown> = {};
        for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
            sanitized[stripLoneSurrogates(key)] = nested;
        }
        return sanitized;
    }
    return value;
};

// Serialize a context to a header-safe string: strip lone surrogates before JSON.stringify (a lone
// surrogate would otherwise be escaped to an ASCII sequence the post-stringify scrub keeps), then
// scrub the remaining header-unsafe code points from the output.
const serializeSafe = (value: unknown): string => scrubHeaderValue(JSON.stringify(value, stripLoneSurrogatesReplacer));

// The Aia-Oidc-Provider header carries the same unbounded, host-supplied hostContext.id. Provider
// selection is an exact-match lookup (fallback vismaonline), so a real id is a short slug; bound it
// like the AiAHostContext header so a pathological id can't emit a multi-KB header a server rejects
// (431). A value this long would never match a provider anyway.
const MAX_PROVIDER_HEADER_LENGTH = 256;

/**
 * Scrubs and length-bounds the host-supplied provider id for the `Aia-Oidc-Provider` header. Unlike
 * {@link serializeHostContextHeader}, the slice here runs on the raw id and scrubHeaderValue runs
 * directly on the sliced code points (no JSON.stringify in between), so a surrogate pair split by the
 * cap is dropped as an over-Latin-1 code point rather than surviving as an escape.
 */
export const scrubProviderHeaderValue = (id: string | undefined): string =>
    scrubHeaderValue((id ?? '').slice(0, MAX_PROVIDER_HEADER_LENGTH));
